import React, { useCallback } from 'react';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	INLINE_DIALOG,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { ToggleableFeatureKey } from '../../common/types.tsx';
import { findDependentFeature } from '../../common/utils.tsx';
import { useFeature } from '../../controllers/board-features/index.tsx';
import { useDependencyModalContext } from '../../controllers/dependency-modal-context/index.tsx';
import { useRedirect } from '../../controllers/use-redirect/index.tsx';
import { useShowFlags } from '../../controllers/use-show-flags/index.tsx';
import { FeatureToggleGroup } from './feature-toggle-group/index.tsx';
import type { Props } from './types.tsx';

export const FeaturePopupContent = ({
	projectUrl,
	boardId,
	update: repositionPopup,
	onNavigateToFeaturesSetting,
	onAddShortcutClick,
}: Props) => {
	const [{ features }, { updateFeature }] = useFeature();
	const { showErrorFlag } = useShowFlags(projectUrl);
	const { openModal } = useDependencyModalContext();
	const redirect = useRedirect(projectUrl);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleItemToggle = useCallback(
		(feature: ToggleableFeatureKey) => {
			const dependentFeature = findDependentFeature(features, feature);
			if (dependentFeature) {
				// set modal
				openModal({
					featureToDisable: features.find((f) => f.feature === feature),
					beneficiaryFeature: dependentFeature,
				});
				return;
			}
			updateFeature({
				boardId,
				feature,
				onError: showErrorFlag,
				onAfterUpdate: redirect,
				analyticsEvent: createAnalyticsEvent({}),
			});

			// reposition popup
			// setTimeout to ensure it happens in the same animation frame as the state update
			setTimeout(repositionPopup, 0);
		},
		[
			features,
			updateFeature,
			boardId,
			showErrorFlag,
			redirect,
			createAnalyticsEvent,
			repositionPopup,
			openModal,
		],
	);

	return (
		<>
			<FeatureToggleGroup
				features={features}
				projectUrl={projectUrl}
				onItemToggle={handleItemToggle}
				onMoreFeatureClick={onNavigateToFeaturesSetting}
				onAddShortcutClick={onAddShortcutClick}
			/>
			<ContextualAnalyticsData sourceName="sidebarFeatureToggle" sourceType={INLINE_DIALOG}>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</>
	);
};
